import {all, call, put, takeLatest} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {AsyncResult, isServerError, Loaded, Loading} from 'Core/models/Async';
import {ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import * as E from 'fp-ts/lib/Either';
import userApi from 'Users/api/userApi';
import {
  registerUser,
  RegistrationActionType,
  setEmail,
  setPhoneNumber,
  setUsername,
  showLoadingProgress,
  validateEmail,
  validatePhoneNumber,
  validateUsername,
} from 'Users/ducks/registration';
import {SavingUserRegistration, UserRegistrationWithError} from 'Users/models/UserRegistration';

export function* validateEmailApi(action: ReturnType<typeof validateEmail>) {
  const validEmail = ValidEmail.create(action.payload.email);

  if (!(validEmail instanceof ValidEmail)) {
    yield put(setEmail(new Loaded(E.left(validEmail))));
  } else {
    yield put(setEmail(new Loading(E.left(ValidEmail.create(action.payload.email)))));

    const result: AsyncResult<any> = yield call(
      userApi.validateExternalEmail,
      action.payload.email,
      action.payload.registrationKey
    );

    const isEmailTaken = E.isLeft(result);

    const mapEmail = (email: string) =>
      isEmailTaken ? E.left(ValidEmail.create(email)) : E.right(ValidEmail.create(email) as ValidEmail);

    const emailValue = new Loaded(mapEmail(action.payload.email));

    yield put(setEmail(emailValue));
  }
}

export function* validateEmailSaga() {
  yield takeLatest(RegistrationActionType.VALIDATE_EMAIL, validateEmailApi);
}
export function* validatePhoneNumberApi(action: ReturnType<typeof validatePhoneNumber>) {
  const validatedPhone = ValidPhoneNumber.create(action.payload.phoneNumber);

  if (!(validatedPhone instanceof ValidPhoneNumber)) {
    yield put(setPhoneNumber(new Loaded(E.left(validatedPhone))));
  } else {
    yield put(setPhoneNumber(new Loading(E.left(ValidPhoneNumber.create(action.payload.phoneNumber)))));

    const result: AsyncResult<any> = yield call(
      userApi.validateExternalPhoneNumber,
      action.payload.phoneNumber,
      action.payload.registrationKey
    );

    const isPhoneNumberTaken = E.isLeft(result);

    const mapPhoneNumber = (phoneNumber: string) =>
      isPhoneNumberTaken
        ? E.left(ValidPhoneNumber.create(phoneNumber))
        : E.right(ValidPhoneNumber.create(phoneNumber) as ValidPhoneNumber);

    const phoneValue = new Loaded(mapPhoneNumber(action.payload.phoneNumber));

    yield put(setPhoneNumber(phoneValue));
  }
}

export function* validatePhoneNumberSaga() {
  yield takeLatest(RegistrationActionType.VALIDATE_PHONE_NUMBER, validatePhoneNumberApi);
}
export function* validateUsernameApi(action: ReturnType<typeof validateUsername>) {
  const {username, registrationKey} = action.payload;
  yield put(setUsername(new Loading(E.left(NonEmptyString.create(action.payload.username)))));

  if (username) {
    const result: AsyncResult<any> = yield call(userApi.validateExternalUsername, username, registrationKey);

    const isUsernameTaken = E.isLeft(result);

    const mapUsername = (username: string) =>
      isUsernameTaken
        ? E.left(NonEmptyString.create(username))
        : E.right(NonEmptyString.create(username) as NonEmptyString);

    const usernameValue = new Loaded(mapUsername(action.payload.username));

    yield put(setUsername(usernameValue));
  } else {
    yield put(setUsername(new Loaded(E.left(NonEmptyString.create(username)))));
  }
}

export function* validateUsernameSaga() {
  yield takeLatest(RegistrationActionType.VALIDATE_USERNAME, validateUsernameApi);
}
export function* registerUserGenerator(action: ReturnType<typeof registerUser>) {
  yield put(showLoadingProgress(SavingUserRegistration.create(action.payload.registration)));

  const result: AsyncResult<unknown> = yield call(
    userApi.registerUser,
    action.payload.firstName,
    action.payload.lastName,
    action.payload.userKey,
    action.payload.registration
  );

  if (E.isRight(result)) {
    yield put(push(CoreRouteConstants.LOGIN));
  } else {
    if (isServerError(result.left)) {
      yield put(
        showLoadingProgress(
          UserRegistrationWithError.create(action.payload.registration, result.left.errorDetails || result.left.message)
        )
      );
    } else {
      yield put(
        showLoadingProgress(UserRegistrationWithError.create(action.payload.registration, result.left.message))
      );
    }
  }
}

export function* registerExternalUserSaga() {
  yield takeLatest(RegistrationActionType.register, registerUserGenerator);
}

export default function*() {
  yield all([validatePhoneNumberSaga(), registerExternalUserSaga(), validateUsernameSaga(), validateEmailSaga()]);
}
